import React from 'react';
import { useVirtualizedGrid } from '../hooks/use-virtualized-grid';

interface VirtualizedGridProps<T> {
    items: T[];
    renderItem: (item: T, index: number) => React.ReactNode;
    startMarker?: React.ReactNode;
    endMarker?: React.ReactNode;
    initialVisibleCount?: number;
    loadMoreThreshold?: number;
    reduceThreshold?: number;
    batchSize?: number;
    minVisibleCount?: number;
    className?: string;
    enabled?: boolean;
    onVisibleCountChange?: (count: number) => void;
}

/**
 * A virtualized grid component that efficiently renders large lists of items by only rendering
 * the visible portion of the list. This component is particularly useful for displaying large
 * datasets while maintaining good performance.
 *
 * The component uses intersection observers to detect when the user scrolls near the start or
 * end of the visible items, and dynamically loads or unloads items as needed.
 *
 * @template T The type of items in the grid
 * @param {T[]} items - The complete list of items to be displayed
 * @param {(item: T, index: number) => React.ReactNode} renderItem - Function to render each item
 * @param {React.ReactNode} [startMarker] - Optional element to render at the start of the list
 * @param {React.ReactNode} [endMarker] - Optional element to render at the end of the list
 * @param {number} [initialVisibleCount] - Initial number of items to render
 * @param {number} [loadMoreThreshold] - Threshold for loading more items when scrolling
 * @param {number} [reduceThreshold] - Threshold for reducing visible items when scrolling up
 * @param {number} [batchSize] - Number of items to load/unload in each batch
 * @param {number} [minVisibleCount] - Minimum number of items to keep visible
 * @param {string} [className] - Additional CSS classes for the grid container
 * @param {boolean} [enabled=true] - Whether virtualization is enabled
 * @param {(count: number) => void} [onVisibleCountChange] - Callback when visible count changes
 *
 * @example
 * ```tsx
 * <VirtualizedGrid
 *   items={largeList}
 *   renderItem={(item) => <ItemComponent {...item} />}
 *   initialVisibleCount={20}
 *   loadMoreThreshold={5}
 *   batchSize={10}
 * />
 * ```
 */
export function VirtualizedGrid<T>({
    items,
    renderItem,
    startMarker,
    endMarker,
    initialVisibleCount,
    loadMoreThreshold,
    reduceThreshold,
    batchSize,
    minVisibleCount,
    className,
    enabled = true,
    onVisibleCountChange,
}: VirtualizedGridProps<T>) {
    const { visibleCount, startRef, endRef } = useVirtualizedGrid({
        totalItems: items.length,
        initialVisibleCount,
        loadMoreThreshold,
        reduceThreshold,
        batchSize,
        minVisibleCount,
        enabled,
        onVisibleCountChange,
    });

    const visibleItems = items.slice(0, visibleCount);

    return (
        <>
            {startMarker && <div ref={startRef}>{startMarker}</div>}
            <div className={className}>
                {visibleItems.map((item, index) => renderItem(item, index))}
            </div>
            {endMarker && <div ref={endRef}>{endMarker}</div>}
        </>
    );
}
